import { useEffect, useRef, useState } from 'react';
import Utils from 'context/utils';

/**
 * SessionCountdown — the one live mm:ss countdown to an acting session's expiry.
 *
 * Shared by the acting pill (bottom-centre indicator) and the user menu, which both display the
 * same live-until-zero deadline for the same 60-minute, non-renewable acting session. Extracted so
 * there is exactly one interval, one unmount cleanup, and one guarded call into
 * `api.checkAndEndExpiredSession()` — the two surfaces may present the result differently
 * (`className`, `prefix`), but the timing, cleanup and zero-handling live in this one place rather
 * than being copy-pasted and risking drift between them.
 *
 * A countdown that merely sits at "0:00" once it runs out would be a lie about a session that is
 * still non-renewable — reaching zero calls `api.checkAndEndExpiredSession()`, the same
 * expiry-and-stop pair the idle timer already runs every 30s, guarded so the call fires exactly
 * once no matter how many further ticks land before the parent re-renders this away.
 *
 * Renders nothing when there is no `expiresAt` to count down to (the session isn't acting, or
 * hasn't hydrated yet).
 */
export default function SessionCountdown({ expiresAt, api, prefix = "Expires in ", className }) {
  const [remainingMs, setRemainingMs] = useState(() => (expiresAt ? expiresAt - Date.now() : 0));
  const endedRef = useRef(false);

  useEffect(() => {
    if ( !expiresAt ) {
      return undefined;
    }

    endedRef.current = false;

    const tick = () => {
      const remaining = expiresAt - Date.now();
      setRemainingMs(remaining);
      if ( remaining <= 0 && !endedRef.current ) {
        endedRef.current = true;
        api.checkAndEndExpiredSession();
      }
    };

    tick();
    const intervalId = setInterval(tick, 1000);
    return () => clearInterval(intervalId);
  }, [expiresAt, api]);

  if ( !expiresAt ) {
    return null;
  }

  return <span className={className}>{prefix}{Utils.formatCountdown(remainingMs)}</span>;
}
